import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchGamics } from '@/store/Gamic';
import apiService from '@/services/apiService';

type PlayGamic = {
  id: string;
  file_url: string;
  title: string;
  image_url: string;
};

type Row = PlayGamic & { _k: string }; // allow duplicates via client key

type ItemState = {
  status: 'queued' | 'downloading' | 'ready' | 'error';
  progress?: number;
  blobUrl?: string;
  mime?: string;
  error?: string;
  filename?: string;
};

const guessMimeFromExt = (url: string): string | undefined => {
  const u = url.split('?')[0].toLowerCase();
  if (u.endsWith('.html') || u.endsWith('.htm')) return 'text/html';
  if (u.endsWith('.png')) return 'image/png';
  if (u.endsWith('.jpg') || u.endsWith('.jpeg')) return 'image/jpeg';
  if (u.endsWith('.pdf')) return 'application/pdf';
  return undefined;
};

const getFilenameFromHeaders = (disposition?: string): string | undefined => {
  if (!disposition) return;
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
  if (m?.[1]) return decodeURIComponent(m[1]);
};

const PlayGamics: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [list, setList] = useState<Row[]>([]);
  const [states, setStates] = useState<Record<string, ItemState>>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ---- stable refs to avoid stale closures ----
  const listRef = useRef<Row[]>([]);
  const statesRef = useRef<Record<string, ItemState>>({});
  const processingRef = useRef(false); // queue runner guard
  const currentControllerRef = useRef<AbortController | null>(null);

  useEffect(() => { listRef.current = list; }, [list]);
  useEffect(() => { statesRef.current = states; }, [states]);

  // Right-side nav refs
  const wheelAccumRef = useRef(0);
  const touchStartYRef = useRef<number | null>(null);
  const navCooldownRef = useRef(false);
  const cooldownMs = 350;
  const wheelThreshold = 80;
  const swipeThreshold = 60;

  // unique key generator
  const seqRef = useRef(0);
  const makeKey = () => {
    seqRef.current += 1;
    return `k${Date.now()}_${seqRef.current}`;
  };

  const api = apiService();
  const LIMIT = 10;

  // Append helper (no dedupe; init queued)
  const appendRows = useCallback((rows: PlayGamic[]) => {
    const mapped: Row[] = rows.map((r) => ({ ...r, _k: makeKey() }));
    setList((prev) => prev.concat(mapped));
    setStates((prev) => {
      const next = { ...prev };
      mapped.forEach((it) => {
        next[it._k] = { status: 'queued', mime: guessMimeFromExt(it.file_url) };
      });
      return next;
    });
  }, []);

  // Initial load (always page 1)
  const loadInitial = useCallback(async () => {
    const res = await dispatch(fetchGamics({ page: 1, limit: LIMIT }));
    appendRows(res?.payload?.data ?? []);
  }, [dispatch, appendRows]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Load more (always page 1; backend random; allow duplicates)
  const loadMoreRandom = useCallback(async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await dispatch(fetchGamics({ page: 1, limit: LIMIT }));
      appendRows(res?.payload?.data ?? []);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, appendRows, isLoadingMore]);

  // ---------- Robust sequential queue runner ----------
  const tickQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    try {
      // Keep processing while there is any queued/error item
      // (error -> we try again next pass)
      for (;;) {
        // pick next candidate
        const items = listRef.current;
        let nextIndex = -1;
        for (let i = 0; i < items.length; i++) {
          const k = items[i]._k;
          const st = statesRef.current[k];
          if (!st || st.status === 'queued' || st.status === 'error') {
            nextIndex = i;
            break;
          }
        }
        if (nextIndex === -1) break; // nothing to do

        const item = items[nextIndex];
        const key = item._k;

        // mark downloading
        setStates((s) => ({ ...s, [key]: { ...(s[key] ?? {}), status: 'downloading' } }));

        // new controller per item
        const ctrl = new AbortController();
        currentControllerRef.current = ctrl;

        try {
          const res = await api.get('/s3/streaming', {
            params: { key: item.file_url },
            responseType: 'blob',
            signal: ctrl.signal as any
          });

          const contentType =
            res.headers?.['content-type'] ||
            res.headers?.['Content-Type'] ||
            statesRef.current[key]?.mime ||
            guessMimeFromExt(item.file_url) ||
            'application/octet-stream';

          const filename =
            getFilenameFromHeaders(
              (res.headers?.['content-disposition'] || res.headers?.['Content-Disposition']) as string
            ) || item.title || 'file';

          const blob = new Blob([res.data], { type: contentType });
          const blobUrl = URL.createObjectURL(blob);

          setStates((s) => ({
            ...s,
            [key]: { status: 'ready', blobUrl, mime: contentType, filename }
          }));
        } catch (err: any) {
          // If canceled (e.g., unmount), re-queue so it can retry later if needed
          const name = err?.name || '';
          if (name === 'CanceledError' || name === 'AbortError') {
            setStates((s) => ({ ...s, [key]: { ...(s[key] ?? {}), status: 'queued' } }));
            break; // stop loop on cancel
          }
          setStates((s) => ({
            ...s,
            [key]: {
              ...(s[key] ?? {}),
              status: 'error',
              error: err?.message || 'Download failed'
            }
          }));
          // continue to next item
        } finally {
          if (currentControllerRef.current === ctrl) currentControllerRef.current = null;
        }
      }
    } finally {
      processingRef.current = false;
    }
  }, [api]);

  // Start/continue queue whenever list or states change (and something is pending)
  useEffect(() => {
    // if any queued/error exists, tick the queue
    const pending = list.some((it) => {
      const st = states[it._k];
      return !st || st.status === 'queued' || st.status === 'error';
    });
    if (pending) {
      // fire and forget
      void tickQueue();
    }
  }, [list, states, tickQueue]);

  // Unmount cleanup ONLY (abort inflight + revoke blob URLs)
  useEffect(() => {
    return () => {
      try { currentControllerRef.current?.abort(); } catch {}
      setStates((s) => {
        Object.values(s).forEach((v) => v.blobUrl && URL.revokeObjectURL(v.blobUrl));
        return s;
      });
    };
  }, []);

  // Keep index in range (looping safe)
  useEffect(() => {
    setCurrentIdx((i) => {
      const n = list.length;
      if (n === 0) return 0;
      return ((i % n) + n) % n;
    });
  }, [list.length]);

  const totalReady = useMemo(
    () => Object.values(states).filter((s) => s.status === 'ready').length,
    [states]
  );

  // Looping navigation
  const goPrev = useCallback(() => {
    setCurrentIdx((i) => {
      const n = list.length;
      if (n === 0) return i;
      return (i - 1 + n) % n;
    });
  }, [list.length]);

  const goNext = useCallback(() => {
    setCurrentIdx((i) => {
      const n = list.length;
      if (n === 0) return i;
      return (i + 1) % n;
    });
  }, [list.length]);

  // Arrow keys
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  // Near-end auto-append (always page 1; duplicates allowed)
  useEffect(() => {
    if (!list.length || isLoadingMore) return;
    const remaining = (list.length - 1) - currentIdx;
    if (remaining <= 3) {
      void loadMoreRandom();
    }
  }, [list.length, currentIdx, isLoadingMore, loadMoreRandom]);

  // Right nav helpers
  const triggerWithCooldown = (fn: () => void) => {
    if (navCooldownRef.current) return;
    navCooldownRef.current = true;
    fn();
    setTimeout(() => (navCooldownRef.current = false), cooldownMs);
  };

  const onWheelNav = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (navCooldownRef.current) return;
    wheelAccumRef.current += e.deltaY;
    if (wheelAccumRef.current > wheelThreshold) {
      wheelAccumRef.current = 0;
      triggerWithCooldown(goNext);
    } else if (wheelAccumRef.current < -wheelThreshold) {
      wheelAccumRef.current = 0;
      triggerWithCooldown(goPrev);
    }
  };

  const onTouchStartNav = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = e.touches[0].clientY;
  };
  const onTouchMoveNav = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (touchStartYRef.current == null || navCooldownRef.current) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (dy <= -swipeThreshold) {
      touchStartYRef.current = e.touches[0].clientY;
      triggerWithCooldown(goNext);
    } else if (dy >= swipeThreshold) {
      touchStartYRef.current = e.touches[0].clientY;
      triggerWithCooldown(goPrev);
    }
  };
  const onClickNav = () => triggerWithCooldown(goNext);

  const currentItem = list[currentIdx];
  const currentState = currentItem ? states[currentItem._k] : undefined;
  const mime = currentItem ? (currentState?.mime || guessMimeFromExt(currentItem.file_url)) : undefined;

  return (
    <div className="flex h-full flex-col gap-3">
      {/* Top info bar */}
      <div className="flex items-center justify-between">
        <div className="text-lg font-bold">
          Loaded: {totalReady}/{list.length}
        </div>
        <div className="text-sm text-gray-600">
          {list.length ? `${currentIdx + 1} / ${list.length}` : '0 / 0'}
        </div>
      </div>

      {/* Viewer + Right Nav Pane */}
      {currentItem ? (
        <div className="relative flex w-full">
          {/* Viewer */}
          <div className="flex-1 p-1">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold">{currentItem.title || 'Untitled'}</div>
              <span
                className={
                  currentState?.status === 'ready'
                    ? 'text-green-600'
                    : currentState?.status === 'downloading'
                    ? 'text-blue-600'
                    : currentState?.status === 'error'
                    ? 'text-red-600'
                    : 'text-gray-500'
                }
              >
                {currentState?.status ?? 'queued'}
              </span>
            </div>

            {/* PREVIEW */}
            {currentState?.status === 'ready' ? (
              <div className="mt-2">
                {mime?.includes('text/html') ? (
                  <div className="h-[600px] w-full overflow-hidden rounded-md border">
                    {/* GAMIC-SANDBOX-V1 (2026-10-03): load publisher HTML from the
                        cookieless S3 origin, not a blob: URL (blob: inherits
                        app.funizzle.com's origin, so allow-same-origin let a
                        gamic script read the admin session / call the API). */}
                    <iframe
                      src={`https://funizzle-files.s3.amazonaws.com/${encodeURI(currentItem.file_url)}`}
                      title={currentItem.title}
                      className="h-full w-full"
                      sandbox="allow-scripts allow-forms allow-pointer-lock allow-same-origin"
                    />
                  </div>
                ) : mime?.startsWith('image/') ? (
                  <img
                    src={currentState.blobUrl}
                    alt={currentItem.title}
                    className="max-h-[520px] w-auto rounded-xl object-contain"
                  />
                ) : mime === 'application/pdf' ? (
                  <div className="h-[520px] w-full overflow-hidden rounded-xl border">
                    <object data={currentState.blobUrl} type="application/pdf" className="h-full w-full">
                      <a href={currentState.blobUrl} target="_blank" rel="noreferrer">
                        Open PDF
                      </a>
                    </object>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <a
                      href={currentState.blobUrl}
                      download={currentState.filename || 'file'}
                      className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
                    >
                      Download
                    </a>
                    <a
                      href={currentState.blobUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border px-3 py-2 text-sm"
                    >
                      Open in new tab
                    </a>
                    <span className="text-xs text-gray-500">{mime}</span>
                  </div>
                )}
              </div>
            ) : currentState?.status === 'downloading' ? (
              <div className="mt-2 h-[600px]">
                <div className="mb-2 text-sm text-gray-600">Downloading…</div>
                <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                  <div
                    className="h-2 bg-blue-500 transition-all"
                    style={{ width: `${currentState.progress ?? 35}%` }}
                  />
                </div>
                {isLoadingMore && (
                  <div className="mt-2 text-xs text-gray-500">Loading more…</div>
                )}
              </div>
            ) : currentState?.status === 'error' ? (
              <div className="h-[600px] text-sm text-red-600">Error: {currentState.error}</div>
            ) : (
              <div className="h-[600px] text-sm text-gray-500">Queued…</div>
            )}
          </div>

          {/* Right nav overlay (loop + swipe/scroll) */}
          <div
            className="relative w-16 shrink-0 select-none"
            onWheel={onWheelNav}
            onTouchStart={onTouchStartNav}
            onTouchMove={onTouchMoveNav}
            onClick={onClickNav}
            style={{ touchAction: 'none' }}
          >
            <div className="absolute inset-y-0 right-0 w-16 rounded-l-xl bg-white/80 backdrop-blur-sm border-l" />
            <div className="pointer-events-none absolute inset-y-0 left-0 right-0 bg-gradient-to-l from-white/70 to-transparent" />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="rotate-90 text-[10px] font-medium text-gray-500">
                Scroll / Swipe → Next (auto-append)
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500">No items.</div>
      )}
    </div>
  );
};

export default PlayGamics;
